import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { isSameDay, formatISO } from 'date-fns';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';

import { AutoPopupModal } from '@/components/widgets/modal';
import { Button } from '@nf/ui';
import { Icon } from '@/components/icons/svg-icons';
import { useAuthToken } from '@/store/auth';
import { useGetSiteConfigs } from '@/hooks/query/use-site-configs';
import { TournamentSlider } from './components/tournament-slider';
import { BetbySlider } from './components/betby-slider';
import { BetbyEsoccerSlider } from './components/betby-esoocer-slider';
import styles from './promotion-activity-modal.module.scss';
import { setKafkaNormal } from '@/hooks/query/use-kafka-log';
import { KafkaNote } from '@nf/utils-common/constants';
import { useAppearanceStore } from '@/store/appearance';
import { ViewEnum } from '@/types';
import { ScoreChallengeSlider } from './components/score-challenge-slider';
import { SportEventSlider } from './components/sport-event-slider';
import { WinnerListSlider } from '@/features/sport-event/components/winner-list-slider';
import { SbrSlider } from './components/sbr-slider';
import { BetBuilderPlusSlider } from './components/bet-builder-plus-slider';
import { PredictionMarketSlider } from './components/prediction-market-slider';
import { WorldCup2026Slider } from './components/world-cup-2026-slider';
import { StreamerSlider } from './components/streamer-slider';
import { useAccountInfo, useAccountStatus } from '@/hooks/query/account';
import { timezoneService } from '@/services/timezone-service';
import { PackPopupType } from '@nf/types/promotion';
import { useDismissPopupStates } from '@/features/promotion/cwc-risk-free-bet/hooks/use-dismiss-popup-states';
import { usePackPopupStates } from '@/features/promotion/cwc-risk-free-bet/hooks/use-pack-popup-states';
import { LabelCheckButton } from '@/components/check-elements/label-check-button';
import { BetterEsoccerBattleSlider } from './components/better-esoccer-battle-slider';
import { useLicSettings } from '@/domains/config/hooks/use-lic-settings';
import { isWithinTimeRange } from '@nf/utils-common/date-time';
import { usePredictionMarket } from '@/hooks/use-prediction-market';
import { useWcPromotionVisible } from '@/domains/wc-promotion/hooks/use-wc-promotion-visible';
import { CorrectScoreAdjSlider } from './components/correct-score-adj-slider';
import { ApiSiteNameEnums } from '@nf/types/site';
import { Fun88VirtualFifaWorldCupSlider } from './components/fun88-virtual-fifa-world-cup-slider';
import {
	We88SeasonalPromotionSlider,
	resolveWe88SeasonalVariant,
} from './components/we88-seasonal-promotion-slider';
import { useMenuTypeList } from '@/hooks/query/use-menu-data';
import { AccountActStatus } from '@nf/utils-common/constants';
import webStorageKeys from '@/constants/web-storage-keys';
import { AutoPopupKey } from '@nf/auto-popup-handler';

export const PromotionActivityModal = () => {
	const t = useTranslations('General');
	const { SiteConfigsData } = useGetSiteConfigs();
	const siteId = SiteConfigsData?.GetMessageID ?? '';
	const { enablePredictionMarketPopup } = usePredictionMarket();
	const view = useAppearanceStore(state => state.view);
	const [isOpen, setIsOpen] = useState(false);
	const [hasBeenClosed, setHasBeenClosed] = useState(false);
	const [isSelected, setIsSelected] = useState(false);
	const authToken = useAuthToken();
	const isBefore = !authToken;
	const isGalaxyView = view === ViewEnum.galaxy;
	const [currentSlide, setCurrentSlide] = useState(0);
	const { user } = useAccountInfo();
	const { dismissPopupStates } = useDismissPopupStates();
	const modalClosedData = localStorage.getItem(webStorageKeys.PROMOTION_ACTIVITY_MODAL_CLOSED);
	const { data: packPopupStates } = usePackPopupStates([PackPopupType.DontShowAgainToday], { shouldCheck: !modalClosedData });

	const isWcPromotionVisible = useWcPromotionVisible();
	const { menuTypeList } = useMenuTypeList();
	const isFun88Site = siteId === ApiSiteNameEnums.Asiabet88.toString() || siteId === ApiSiteNameEnums.JBO.toString() || siteId === ApiSiteNameEnums.SampleSite.toString();
	const hasMenuType51 = menuTypeList.some(item => item.modeId == 51);
	const { data: actStatus } = useAccountStatus();
	const {
		enableESoccerBattlePromotion,
		enableWorldCupFeverPopup,
		enableFootballLeagueResumesPopup,
		enableFootballLeagueFixtureFrenzyPopup,
		enableStreamerBanner,
		enableListPopupBanner,
	} = useLicSettings();
	const activeWe88Variant = resolveWe88SeasonalVariant(
		{
			enableWorldCupFeverPopup,
			enableFootballLeagueResumesPopup,
			enableFootballLeagueFixtureFrenzyPopup,
		},
		user?.SubsidiaryOpratorId,
	);

	const handleClose = async () => {
		if (isSelected) {
			// 如果勾選了 "今日不再顯示"，則將狀態和時間戳記儲存到 localStorage
			const data = {
				timestamp: timezoneService.clientToTimezone(formatISO(new Date()), -4)
			};
			localStorage.setItem(webStorageKeys.PROMOTION_ACTIVITY_MODAL_CLOSED, JSON.stringify(data));
			dismissPopupStates(PackPopupType.DontShowAgainToday);
		}
		setIsOpen(false);
		setHasBeenClosed(true);
	};

	// 活動彈窗的配置，包含：是否啟用促銷活動 Slider、活動顯示的順序
	const promotionActivities = [
		// WC Promotion always be the first one, other promotions should be after this one
		// Other promotions's on-line time decides the order
		{
			enable: enablePredictionMarketPopup,
			slider: <PredictionMarketSlider onClose={handleClose} />,
			onDontShowAgainToday: () => {
			},
			onClose: () => {
				setKafkaNormal({
					note: KafkaNote.PredictionMarket,
					type: 1,
					action: 2
				});
			}
		},
		{
			enable: isFun88Site && hasMenuType51,
			slider: <Fun88VirtualFifaWorldCupSlider onClose={handleClose} />
		},
		{
			enable: activeWe88Variant !== null,
			slider: (
				<We88SeasonalPromotionSlider
					variant={activeWe88Variant!}
					onClose={handleClose}
				/>
			),
		},
		{
			enable: !!SiteConfigsData?.CorrectScoreSpreadAdjust?.length,
			slider: <CorrectScoreAdjSlider onClose={handleClose} />,
		},
		{
			enable: isWcPromotionVisible,
			slider: <WorldCup2026Slider onClose={handleClose} />,
			onClose: () => {
				setKafkaNormal({
					note: KafkaNote.WcPromotion2026,
					type: 1,
					action: 3
				});
			}
		},
		{
			enable: enableESoccerBattlePromotion,
			slider: <BetterEsoccerBattleSlider onClose={handleClose} />,
			trackTagName: 'login-popup-banner-112',
			// onClose: () => {
			// 	setKafkaNormal({
			// 		note: KafkaNote.BetterEsoccerBattle,
			// 		type: 1,
			// 		action: 1
			// 	});
			// }
		},
		{
			enable: actStatus === AccountActStatus.Deposited && enableStreamerBanner,
			slider: <StreamerSlider onClose={handleClose} />,
		},
		{
			enable: SiteConfigsData?.EnableBetBuilderPlusPopup && isWithinTimeRange({ to: 1770220799000 }), // 2026/02/04 23:59:59 GMT+8
			slider: <BetBuilderPlusSlider onClose={handleClose} />,
			onClose: () => {
				setKafkaNormal({
					note: KafkaNote.BetBuilderPlus,
					type: view == ViewEnum.mars ? 1 : 2,
					action: 1
				});
			}
		},
		{
			enable: SiteConfigsData?.EnableSportEventPopup && isGalaxyView,
			slider: <SportEventSlider onClose={handleClose} />,
			onDontShowAgainToday: () => {
				setKafkaNormal({
					note: KafkaNote.KingBack,
					type: 1,
					action: isSelected ? 4 : 3
				});
			},
			onClose: () => {
				setKafkaNormal({
					note: KafkaNote.KingBack,
					type: 1,
					action: 2
				});
			}
		},
		{
			enable: SiteConfigsData?.EnableBetbyEsoccerPopup,
			slider: <BetbyEsoccerSlider onClose={handleClose} />,
			onClose: () => {
				setKafkaNormal({
					note: KafkaNote.Betby_eSoccer,
					type: view == ViewEnum.mars ? 1 : 2,
					action: 7
				});
			}
		},
		{
			enable: SiteConfigsData?.EnableBetbyPopup,
			slider: <BetbySlider onClose={handleClose} />,
			onDontShowAgainToday: () => {
				setKafkaNormal({
					note: KafkaNote.Betby,
					type: view == ViewEnum.mars ? 1 : 2,
					action: isSelected ? 9 : 8
				});
			},
			onClose: () => {
				setKafkaNormal({
					note: KafkaNote.Betby,
					type: view == ViewEnum.mars ? 1 : 2,
					action: 7
				});
			}
		},
		{
			enable: SiteConfigsData?.EnableCwcJackpotPopup,
			slider: <ScoreChallengeSlider onClose={handleClose} />,
			onDontShowAgainToday: () => {
				setKafkaNormal({
					note: KafkaNote.CWCJackpot,
					type: 1,
					action: isSelected ? 43 : 42
				});
			},
			onClose: () => {
				setKafkaNormal({
					note: KafkaNote.CWCJackpot,
					type: 1,
					action: 2
				});
			}
		},
		// {
		// 	enable: SiteConfigsData?.IsShowTournamentPromotion,
		// 	slider: <TournamentSlider onClose={handleClose} />,
		// },
		{
			enable: SiteConfigsData?.EnableSportEventWinnerList && isGalaxyView,
			slider: <WinnerListSlider />,
			onDontShowAgainToday: () => {
				// Add Kafka or analytics event if needed
			},
			onClose: () => {
				// Add Kafka or analytics event if needed
			}
		},
		{
			enable: (user?.EnableSBRWidget || isBefore) && isWithinTimeRange({ to: 1766678399000 }), // 2025/12/25 23:59:59 GMT+8
			slider: <SbrSlider onClose={handleClose} />,
			onDontShowAgainToday: () => {
			},
			onClose: () => {
			}
		},
	];
	const enabledPromotions = promotionActivities
		.filter(item => item.enable);
	const activePromotionCount = enabledPromotions.length;

	useEffect(() => {
		// 當以下任一條件成立時，不顯示彈窗：
		// 1. 用戶未登入
		// 2. 所有活動都未啟用
		if (isBefore || activePromotionCount <= 0 || enableListPopupBanner) {
			setIsOpen(false);
			return;
		}

		if (modalClosedData) {
			const { timestamp } = JSON.parse(modalClosedData);
			const today = timezoneService.clientToTimezone(formatISO(new Date()), -4);
			const sameDay = isSameDay(today, timestamp);
			if (sameDay) {
				setIsOpen(false);
			} else {
				localStorage.removeItem(webStorageKeys.PROMOTION_ACTIVITY_MODAL_CLOSED);
				setIsOpen(true);
			}
		} else {
			const isShowAgainTodayVisible = packPopupStates?.[0].IsShow;
			isShowAgainTodayVisible ? setIsOpen(true) : setIsOpen(false);
		}
	}, [isBefore, activePromotionCount, packPopupStates]);

	const handleCheck = async (event: React.MouseEvent) => {
		event.preventDefault();
		const currentDisplayingPromotion = enabledPromotions[currentSlide];
		const onDontShowAgainToday = currentDisplayingPromotion.onDontShowAgainToday;
		if (typeof onDontShowAgainToday === 'function') {
			onDontShowAgainToday();
		}
		setIsSelected(prevIsChecked => !prevIsChecked);
	};

	return (
	// 彈窗的顯示邏輯：
	// 1. 如果使用者未登入，不顯示彈窗
	// 2. 如果所有活動都未啟用，不顯示彈窗
	// 3. 如果有任何活動啟用，則根據 localStorage 的記錄決定是否顯示彈窗
	// 4. 如果使用者未勾選 "今日不再顯示"，則會再顯示彈窗
	// 5. 如果使用者有勾選 "今日不再顯示"，且在同一天，不會再顯示彈窗
	// 6. 如果使用者有勾選 "今日不再顯示"，且不在同一天，則會顯示彈窗

		<AutoPopupModal
			popupKey={AutoPopupKey.PromotionActivity}
			isOpen={isOpen && !hasBeenClosed}
			closeModal={handleClose}
			variant="event"
			showHeader={false}
			isBackdropClose={false}
			className={styles.modal}
		>
			<div className={styles.closeButton}>
				<Button variant="icon" onClick={() => {
					handleClose();
					const currentDisplayingPromotion = enabledPromotions[currentSlide];
					const onCloseCallback = currentDisplayingPromotion.onClose;
					if (typeof onCloseCallback === 'function') {
						onCloseCallback();
					}
				}}
				>
					<Icon name="close-round-line" />
				</Button>
			</div>
			<Swiper
				key={`swiper-${activePromotionCount}`}
				onSlideChange={(swiper) => {
					if (enabledPromotions[swiper.realIndex].trackTagName && window.Wauding?.trackImpression) {
						window.Wauding.trackImpression({ tagName: enabledPromotions[swiper.realIndex].trackTagName });
					}
					setCurrentSlide(swiper.realIndex);
				}}
				autoplay={{
					delay: 5000,
					disableOnInteraction: false,
				}}
				speed={1000}
				loop={activePromotionCount > 1}
				pagination={true}
				navigation={{
					nextEl: `.${styles.swiperNextButton}`,
					prevEl: `.${styles.swiperPrevButton}`,
				}}
				modules={[Autoplay, Navigation, Pagination]}
				className={styles.activitySwiper}
			>
				{/* custom navigation */}
				{activePromotionCount > 1 ? (
					<>
						<div className={styles.swiperPrevButton}><Icon name="arrow-prev" /></div>
						<div className={styles.swiperNextButton}><Icon name="arrow-prev" /></div>
					</>
				) : null}
				
				{/* slides */}
				{enabledPromotions.map((item, index) => (
					<SwiperSlide 
						key={index} 
						className={styles.activitySlide}
					>
						{item.slider}
					</SwiperSlide>
				))}
			</Swiper>
			<LabelCheckButton
				selected={isSelected}
				onClick={(event) => handleCheck(event)}
				className={styles.show}
			>
				{t('lbl_TWPopupShowAgain')}
			</LabelCheckButton>
		</AutoPopupModal>
	);
};